// Angular
import { Injectable } from "@angular/core";

// Third party
import * as _ from 'lodash';
import moment from "moment";

// Local library
import { GUEST, IGetEventsDetailsResponse } from "@amwager/util-ts";
import { WagerFacade } from "../+state/wager.facade";
import { SessionService } from "@amwager/data-access";
import { AccountFacade } from "@amwager/account/data-access";
import { Observable, of } from "rxjs";

@Injectable()
export class WagerService {

  events: any;
  selectedEvent: any;

  constructor(
    private readonly accountFacade: AccountFacade,
    private readonly wagerFacade: WagerFacade,
    private readonly sessionService: SessionService
  ) {}

  buildPoolTotals(
    fromStoragePoolTotal: any[],
    eventPoolTotal: Record<string, number>,
    fromTotalHandle: number,
    fromPoolPrices: any[],
    fromPoolsTotal: any[],
    raceId: number
  ): { fromPoolsTotal: any[], fromTotalHandle: number } {
    const storagePoolTotal = fromStoragePoolTotal;
    const poolsTotal = _.mapValues(_.keyBy(eventPoolTotal, 'Id'), 'Handle');
    const newArrayPoolTotal = _.map(poolsTotal, (value, key) => ({
      key,
      value,
      display: false
    }));

    _.remove(storagePoolTotal, { race: raceId });

    const gapMap = {
      DBL: 1, TRP: 2, PK3: 2, PK4: 3,
      PK5: 4, PK6: 5, PK7: 6, PK8: 7,
      PK9: 8, P10: 9, P11: 10, P12: 11
    };

    const monitorArray = _.chain(newArrayPoolTotal)
      .filter((vP: any) => !_.isNil(vP.value) && vP.value !== 0)
      .map((vP: any) => {
        const gap = gapMap[vP.key as keyof typeof gapMap] || 0;
        return gap > 0
          ? {
            startRace: raceId,
            endRace: raceId + gap,
            poolName: vP.key,
            poolTotal: vP.value
          }
          : null;
      })
      .compact()
      .value();

    if (monitorArray.length > 0) {
      storagePoolTotal.push({
        race: raceId,
        details: monitorArray
      });
    }

    const allDetails = _.flatMap(fromStoragePoolTotal, 'details');

    _.forEach(allDetails, d => {
      if (!_.some(fromPoolsTotal, { Id: d.poolName }) && raceId === d.endRace) {
        fromPoolsTotal = [
          ...fromPoolsTotal || [],
          {
            Handle: d.poolTotal,
            Id: d.poolName
          }
        ];
      }

      if (d.poolName === 'DBL' && raceId === d.endRace) {
        fromPoolsTotal = [
          ...(fromPoolsTotal || []).filter(p => p.Id !== 'DBL'),
          {
            Handle: d.poolTotal,
            Id: 'DBL'
          }
        ];
      }
    });


    const poolIds = _.chain(fromPoolPrices)
      .filter(item => _.has(item, 'Prices') && item?.Prices?.length > 0)
      .map('PoolId')
      .value();
    // const poolIds = _.filter(fromPoolPrices, item =>
    //   _.has(item, 'Prices') && item?.Prices?.length > 0
    // );
    // const poolIds = _.map(fromPoolPrices, 'PoolId');
    fromPoolsTotal = _.filter(fromPoolsTotal, item => _.includes(poolIds, item.Id));
    fromTotalHandle = _.sumBy(fromPoolsTotal, 'Handle');

    return {
      fromPoolsTotal,
      fromTotalHandle
    };
  }

  toDateUTC(date: any): string {
    return moment(date).format('MM-DD-YYYY[Z]');
  }

   /**
   * True when the event's track type matches the user's event filter
   * @param {number} type Track type id
   * @param {string[]} selected Enabled event types
   * @param {string} countryCode Event country code
   * @returns {boolean} True when the track type is enabled
   */
   selectedTrackType(type: number, selected?: string[], countryCode?: string): boolean {

    let result: boolean = false;
    let trackTypeIs: string = '';

    switch(type) {
      // Quarterhorse included based on frontend repo
      case 1:
        trackTypeIs = 'Thoroughbred';
        break;

      case 2:
        trackTypeIs = 'Greyhound';
        break;

      case 3:
        trackTypeIs = 'Quarterhorse';
        break;

      case 4:
        trackTypeIs = 'Harness';
        break;
    }

    // !type &&
    if (
      (countryCode === null || countryCode !== 'US') &&
      !selected?.includes('International')
    ) {
      return result;
    }

    if (
      countryCode !== null &&
      countryCode !== 'US' &&
      selected?.includes('International')
    ) {
      trackTypeIs = 'International';
    }

    if (selected?.includes(trackTypeIs)) {
      result = true;
    }

    return result;
  }

  /**
   * Sort events by MTP or Name and pick the selected event
   * @param {string} sortBy 'MTP' or 'Name'
   * @param {string[]} eventTypeFilter Event types to include
   * @param {any} prevSelectedEvent Previously-selected event
   * @param {boolean} triggerFromPreference True when triggered by preference change
   * @param {boolean} triggerFromBetResult True when triggered by a bet result
   * @param {boolean} triggerFromVideoReplay True when triggered by a replay video
   * @param {any} latestFinishedEvent Latest finished event
   * @param {any} emptyEvent Empty event placeholder
   * @param {IGetEventsDetailsResponse[]} allEvents All available events
   * @returns {Promise<Observable<any>>} Observable of { events, selectedEvent, ... }
   */
  async onSelectSortEvents(
    sortBy: string,
    eventTypeFilter: string[],
    prevSelectedEvent: any,
    triggerFromPreference: boolean,
    triggerFromBetResult: boolean,
    triggerFromVideoReplay: boolean,
    latestFinishedEvent: any,
    emptyEvent: any,
    allEvents: IGetEventsDetailsResponse[]
  // ): any {
  ): Promise<Observable<any>> {

    if (!allEvents.length) {
      // return {
      //   events: [],
      //   selectedEvent: {}
      // };
      return of({
        events: this.events,
        selectedEvent: this.selectedEvent,
        triggerFromPreference: false,
        triggerFromBetResult: false
      });
    }

    // const today = moment().utc();
    let x: IGetEventsDetailsResponse[] = [];

    if (sortBy === 'MTP') {
      x = _.filter(allEvents, (event: IGetEventsDetailsResponse) =>
        this.selectedTrackType(event?.ToteResponse?.EventDetail?.TrackType, eventTypeFilter, event?.CountryCode) === true
      );
    }

    if (sortBy === 'Name') {
      x = _.concat(
        _.orderBy(
          // others
          _.filter(allEvents, (event: IGetEventsDetailsResponse) => event?.ToteResponse?.EventDetail?.currentRace?.RaceStatus !== 3 &&
            this.selectedTrackType(event?.ToteResponse?.EventDetail?.TrackType, eventTypeFilter, event?.CountryCode) === true
          ),
          ['ToteResponse.EventDetail.EventName'],
          ['asc']
        ),
        _.orderBy(
          // final
          _.filter(allEvents, (event: IGetEventsDetailsResponse) => event?.ToteResponse?.EventDetail?.currentRace?.RaceStatus === 3 &&
            this.selectedTrackType(event?.ToteResponse?.EventDetail?.TrackType, eventTypeFilter, event?.CountryCode) === true
          ),
          ['ToteResponse.EventDetail.EventName'],
          ['asc']
        )
      );
    }

    this.events = x;
    this.selectedEvent = this.events[0];

    if (prevSelectedEvent !== undefined) {

      const updateEventRaceStatus = _.find(
        this.events, (event: IGetEventsDetailsResponse) =>
          event.id === prevSelectedEvent?.id
      );

      // OLD: fell back to events[0] when the user's event wasn't found,
      // hijacking their track to a random one on any race final.
      // if (updateEventRaceStatus)
      //   this.selectedEvent = updateEventRaceStatus;

      // FIX: keep the user's track on background refreshes — fresh copy
      // if found, else their own event (never events[0]).
      this.selectedEvent = updateEventRaceStatus || prevSelectedEvent;
    }

    // for wagering preference change display
    // Thoroughbred, Quarterhorse, Harness, Greyhound, International
    if (triggerFromPreference) {
      triggerFromPreference = false;
      prevSelectedEvent = this.events[0];
      this.selectedEvent = this.events[0];

      // refresh event track, race
      this.wagerFacade.selectedEventTrack('WagerComponent:onSelectSortEvents', this.selectedEvent);

      this.wagerFacade.selectedRace(
        'WagerComponent:onSelectSortEvents',
        {
          ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
          // ...currentRace as any,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
        }
      );
    }

    if (triggerFromBetResult) {
      triggerFromBetResult = false;
      prevSelectedEvent = latestFinishedEvent;
      this.selectedEvent = latestFinishedEvent;

      // refresh event track, race
      this.wagerFacade.selectedEventTrack('WagerComponent:onSelectSortEvents', this.selectedEvent);

      this.wagerFacade.selectedRace(
        'WagerComponent:onSelectSortEvents',
        {
          ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
          // ...currentRace as any,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
        }
      );

      this.wagerFacade.setLatestFinishedEvent(emptyEvent);
    }

    // trigger from video replay
    if (triggerFromVideoReplay) {
      triggerFromVideoReplay = false;
      prevSelectedEvent = latestFinishedEvent;
      this.selectedEvent = latestFinishedEvent;

      // refresh event track, race
      this.wagerFacade.selectedEventTrack('WagerComponent:onSelectSortEvents', this.selectedEvent);

      // get latest finish race
      const currentRace = _.findLast(
        this.selectedEvent?.ToteResponse?.EventDetail?.Races, (race) => {
          return race.RaceStatus === 3;
        }
      );

      // const currentRace = _.find(
      //   this.selectedEvent?.ToteResponse?.EventDetail?.Races, race => _.has(race, 'Current')
      // );

      this.wagerFacade.selectedRace(
        'WagerComponent:onSelectSortEvents',
        {
          // ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
          ...currentRace as any,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
        }
      );
    }

    this.wagerFacade.getSelectedEventTrack$().subscribe((callback) => {
      if (callback === undefined) {
        if (
          this.sessionService.getUserEmail() === GUEST
          || this.sessionService.getRoleId() === 9
        ) {

          // refresh event track, race
          this.wagerFacade.selectedEventTrack('WagerComponent:onSelectSortEvents', this.selectedEvent);

          this.wagerFacade.selectedRace(
            'WagerComponent:onSelectSortEvents',
            {
              ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
              runId: this.selectedEvent?.ToteResponse?.RunId,
              eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
            }
          );
        }
      }
    });

    // return {
    //   events: this.events,
    //   selectedEvent: this.selectedEvent
    // };

    return of({
      events: this.events,
      selectedEvent: this.selectedEvent,
      triggerFromPreference: false,
      triggerFromBetResult: false
    });
  }

  /**
   * Fetch saved/active/close bet slips for the given run
   * @param {string} runId Current run id
   * @returns {void}
   */
  getBetSlip(runId: string): void {
    if (!runId) return;

    const statuses = {
      1: 'saved',
      3: 'active',
      6: 'close'
    };

    _.map([1, 3, 6], (status) => {
      this.wagerFacade.getBetsAPI(
        {
          runId,
          ticketStatus: status,
          offset: 0,
          next: 500
        },
        (statuses as any)[status],
        'WagerComponent:getBetSlip'
      );
    });
  }

  /**
   * Clear active legs and couple-entry markers in the bet table
   * @param {any} elementRef DOM element reference
   * @returns {void}
   */
  clearLegRoundSelector(elementRef: any): void {
    const divLegs = elementRef.nativeElement.querySelectorAll('.table-bet [class^="runner"]');
    // const divLegs = this.elementRef.nativeElement.querySelectorAll('.table-bet [class^="leg"]');
    divLegs.forEach((leg: HTMLElement) => {
      if (leg?.classList.contains('active') || leg?.classList.contains('couple-entry'))
        leg?.classList.remove('active');
        leg?.classList.remove('couple-entry');
        leg.innerHTML = '';
    });
  }

  /**
   * Observable of the user's preferred favorite events
   * @returns {Observable<any>} Favorites list stream
   */
  observerPreferenceFavorites(): Observable<any> {
    return this.accountFacade.getPreferenceFavorites$();
  }

  /**
   * Wrapper around the environment isOnline$ observable
   * @param {any} isOnline$ Connectivity factory function
   * @param {string} environment Env identifier passed to the factory
   * @returns {Observable<any>} Connectivity stream
   */
  checkConnectivity(isOnline$: any, environment: string): Observable<any> {
    return isOnline$(environment);
  }

  /**
   * Compute Minutes-To-Post for the selected race (UTC-safe)
   * @param {any} selectedRace Currently-selected race
   * @param {any} selectedEvent Currently-selected event (fallback PostTime)
   * @returns {number} MTP in whole minutes, or POSITIVE_INFINITY when missing
   */
  getCurrentRaceMTP(selectedRace: any, selectedEvent: any): number {
    const postTime = selectedRace?.PostTime
      ?? selectedEvent?.ToteResponse?.EventDetail?.currentRace?.PostTime;

    if (!postTime) return Number.POSITIVE_INFINITY;

    const post = new Date(postTime + 'Z').getTime();

    return Math.floor((post - Date.now()) / 60000);
  }

  /**
   * True when the live-video button should be enabled
   * @param {any} selectedRace Currently-selected race
   * @param {any} selectedEvent Currently-selected event
   * @returns {boolean} True when video may be played
   */
  isLiveVideoAllowed(selectedRace: any, selectedEvent: any): boolean {
    const eventStatus = selectedEvent?.ToteResponse?.EventDetail?.EventStatus;
    const raceStatus = selectedRace?.RaceStatus;

    if (eventStatus !== 'Open') return false;

    if (raceStatus === 3 || raceStatus === 4) return false;

    if (this.getCurrentRaceMTP(selectedRace, selectedEvent) > 5) return false;
    // if (this.getCurrentRaceMTP(selectedRace, selectedEvent) > 0) return false;

    return true;
  }
}
